import React, { useEffect, useState, createContext, useContext } from 'react';
type Message = {
  id: string;
  content: string;
  sender: 'user' | 'ai';
  timestamp: Date;
  suggestions?: string[];
  products?: any[];
};
type CustomerPreference = {
  destination?: string;
  budget?: string;
  duration?: string;
  groupSize?: string;
  interests?: string[];
  travelDates?: string;
};
interface ChatContextType {
  messages: Message[];
  isOpen: boolean;
  customerPreferences: CustomerPreference;
  setIsOpen: (isOpen: boolean) => void;
  sendMessage: (content: string) => void;
  clearChat: () => void;
  updateCustomerPreferences: (prefs: Partial<CustomerPreference>) => void;
}
const ChatContext = createContext<ChatContextType | undefined>(undefined);
export const ChatProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [messages, setMessages] = useState<Message[]>([{
    id: 'welcome',
    content: "Hello! I'm your AI travel assistant. How can I help you today?",
    sender: 'ai',
    timestamp: new Date(),
    suggestions: ['Find destinations within a budget', 'Suggest family-friendly tours', 'Compare vacation packages', 'Check availability for specific dates']
  }]);
  const [isOpen, setIsOpen] = useState(false);
  const [customerPreferences, setCustomerPreferences] = useState<CustomerPreference>({});
  const sendMessage = (content: string) => {
    if (!content.trim()) return;
    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      content,
      sender: 'user',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    // Process the message and generate AI response
    setTimeout(() => {
      const aiResponse = generateAIResponse(content, customerPreferences);
      setMessages(prev => [...prev, aiResponse]);
      // Update customer preferences based on message content
      const extractedPrefs = extractPreferences(content);
      if (extractedPrefs) {
        updateCustomerPreferences(extractedPrefs);
      }
    }, 1000);
  };
  const clearChat = () => {
    setMessages([{
      id: 'welcome',
      content: "Hello! I'm your AI travel assistant. How can I help you today?",
      sender: 'ai',
      timestamp: new Date(),
      suggestions: ['Find destinations within a budget', 'Suggest family-friendly tours', 'Compare vacation packages', 'Check availability for specific dates']
    }]);
    setCustomerPreferences({});
  };
  const updateCustomerPreferences = (prefs: Partial<CustomerPreference>) => {
    setCustomerPreferences(prev => ({
      ...prev,
      ...prefs
    }));
  };
  // Mock function to extract customer preferences from message
  const extractPreferences = (message: string): Partial<CustomerPreference> | null => {
    const preferences: Partial<CustomerPreference> = {};
    // Extract destination
    const destinationMatch = message.match(/(?:to|in|for)\s+([A-Za-z\s]+)(?:tour|vacation|trip|holiday)/i);
    if (destinationMatch) {
      preferences.destination = destinationMatch[1].trim();
    }
    // Extract budget
    const budgetMatch = message.match(/(?:under|around|about)\s+\$(\d+[,\d]*)/i);
    if (budgetMatch) {
      preferences.budget = `$${budgetMatch[1]}`;
    }
    // Extract duration
    const durationMatch = message.match(/(\d+)[\s-](?:day|night)/i);
    if (durationMatch) {
      preferences.duration = `${durationMatch[1]} days`;
    }
    // Extract group size
    const groupMatch = message.match(/(?:family of|group of|for)\s+(\d+)/i);
    if (groupMatch) {
      preferences.groupSize = groupMatch[1];
    }
    return Object.keys(preferences).length > 0 ? preferences : null;
  };
  // Mock function to generate AI responses
  const generateAIResponse = (message: string, preferences: CustomerPreference): Message => {
    // Check for specific commands
    if (message.toLowerCase().includes('show me these products')) {
      return {
        id: Date.now().toString(),
        content: 'Here are the products that match your requirements:',
        sender: 'ai',
        timestamp: new Date(),
        products: getMockProducts(preferences)
      };
    }
    if (message.toLowerCase().includes('create quote')) {
      return {
        id: Date.now().toString(),
        content: "I've prepared a quote based on your requirements. You can customize it further before sending it to the customer.",
        sender: 'ai',
        timestamp: new Date(),
        suggestions: ['Proceed to quote wizard', 'Modify requirements', 'Save for later']
      };
    }
    if (message.toLowerCase().includes('similar alternatives')) {
      return {
        id: Date.now().toString(),
        content: 'Here are some alternative options that might interest your customer:',
        sender: 'ai',
        timestamp: new Date(),
        products: getMockProducts(preferences, true)
      };
    }
    // General response with extracted information
    if (Object.keys(preferences).length > 0) {
      let response = "Based on what you've told me, ";
      if (preferences.destination) {
        response += `your customer is interested in ${preferences.destination}. `;
      }
      if (preferences.budget) {
        response += `Their budget is around ${preferences.budget}. `;
      }
      if (preferences.duration) {
        response += `They're looking for a ${preferences.duration} trip. `;
      }
      if (preferences.groupSize) {
        response += `They'll be traveling with ${preferences.groupSize} people. `;
      }
      response += 'Here are some recommendations:';
      return {
        id: Date.now().toString(),
        content: response,
        sender: 'ai',
        timestamp: new Date(),
        products: getMockProducts(preferences),
        suggestions: ['Show me more options', 'Create a quote for these', 'Filter by price', "What's the best time to visit?"]
      };
    }
    // Default response
    return {
      id: Date.now().toString(),
      content: 'I can help you find the perfect travel package for your customer. Could you provide more details about their preferences, budget, or destination?',
      sender: 'ai',
      timestamp: new Date(),
      suggestions: ['Find family-friendly destinations', 'Suggest luxury experiences', 'Show budget-friendly options', 'Compare popular packages']
    };
  };
  // Mock function to generate product recommendations
  const getMockProducts = (preferences: CustomerPreference, alternatives = false): any[] => {
    const mockProducts = [{
      id: 1,
      title: 'European Explorer',
      destination: 'Europe',
      duration: '10 days',
      price: 2899,
      image: 'https://images.unsplash.com/photo-1552832230-c0197dd311b5?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
      confidence: 0.92
    }, {
      id: 2,
      title: 'Asian Adventure',
      destination: 'Japan',
      duration: '12 days',
      price: 3199,
      image: 'https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
      confidence: 0.85
    }, {
      id: 3,
      title: 'Tropical Paradise',
      destination: 'Bali',
      duration: '7 days',
      price: 1899,
      image: 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
      confidence: 0.78
    }];
    // Return alternative products if requested
    if (alternatives) {
      return [{
        id: 4,
        title: 'Mediterranean Cruise',
        destination: 'Greece & Italy',
        duration: '8 days',
        price: 2499,
        image: 'https://images.unsplash.com/photo-1570077188670-e3a8d69ac5ff?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        confidence: 0.75
      }, {
        id: 5,
        title: 'Alpine Adventure',
        destination: 'Switzerland',
        duration: '6 days',
        price: 2199,
        image: 'https://images.unsplash.com/photo-1491555103944-7c647fd857e6?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        confidence: 0.72
      }];
    }
    return mockProducts;
  };
  return <ChatContext.Provider value={{
    messages,
    isOpen,
    customerPreferences,
    setIsOpen,
    sendMessage,
    clearChat,
    updateCustomerPreferences
  }} data-id="element-4282">
      {children}
    </ChatContext.Provider>;
};
export const useChat = () => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};